import { ChevronRight } from '@mui/icons-material'
import { GoBackButton } from 'NewVersion/components/UI/GoBack/GoBackButton'
import 'NewVersion/styles/AccreditacioEntitySelector.scss'
import 'NewVersion/styles/EntitySelector.scss'
import { useTranslation } from 'react-i18next'

/**
 * AccreditacioEntitySelector - Component for selecting an entity to accredit as
 *
 * Displays entities as a selectable card list.
 *
 * @param {Array} entities - Array of pre-filtered entity objects
 * @param {Function} onEntitySelected - Callback when entity selected
 * @param {Function} onBack - Callback for back button
 * @param {string} title - Optional title for the screen
 */
export const AccreditacioEntitySelector = ({
  entities = [],
  onEntitySelected,
  onBack,
  title = null,
}) => {
  const { t } = useTranslation()

  const isLoading = entities.length === 0 && !title

  if (isLoading) {
    return <p>{t('loading', 'Loading...')}</p>
  }

  return (
    <div className="accreditation-entity-selector">
      <div className="accreditation-entity-selector__header">
        <GoBackButton
          text={t('back', 'Back')}
          onClick={onBack}
          dataTest="accreditation-entity-back-btn"
        />
        {title && (
          <h2
            className="accreditation-entity-selector__title"
            data-test="accreditation-entity-selector-title"
          >
            {title}
          </h2>
        )}
      </div>

      {entities.length > 0 && (
        <div className="accreditation-entity-selector__list-container">
          <div className="entity-selector__list">
            {entities.map((entity, index) => (
              <AccreditacioEntityCard
                key={entity.path || index}
                entity={entity}
                onSelect={() => onEntitySelected(entity)}
              />
            ))}
          </div>
        </div>
      )}

      {entities.length === 0 && (
        <p className="entity-selector__empty">{t('no_entities_found', 'No entities found')}</p>
      )}
    </div>
  )
}

/**
 * AccreditacioEntityCard - Individual entity card for accreditation selection
 */
const AccreditacioEntityCard = ({ entity, onSelect }) => {
  const { t } = useTranslation()
  const entityYear = entity.year || entity.path?.split('/')[1] || '—'

  return (
    <div
      className="entity-selector-item"
      onClick={onSelect}
      data-test="accreditation-entity-card"
      role="button"
      tabIndex={0}
      aria-label={t('select_entity', 'Select entity {{entity}}', {
        entity: entity.title,
      })}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onSelect()
        }
      }}
    >
      <div className="entity-selector-item__avatar">{entity.title?.[0]?.toUpperCase() || '?'}</div>

      <div className="entity-selector-item__content">
        <p className="entity-selector-item__title">
          {entity.title} ({entityYear})
        </p>

        <EntityTypeBadge tipus={entity.tipus} className="entity-selector-item__tipo-badge" />
      </div>

      <div className="entity-selector-item__select-icon" aria-hidden="true">
        <ChevronRight />
      </div>
    </div>
  )
}

const getEntityTypeClass = (tipus) =>
  tipus === 'artista' || tipus === 'representant' ? tipus : 'default'

const EntityTypeBadge = ({ tipus, className }) => {
  const { t } = useTranslation()

  if (!tipus) {
    return null
  }

  return (
    <span
      className={`${className} ${className}--${getEntityTypeClass(tipus)}`}
      data-test="accreditation-entity-type-badge"
    >
      {t(tipus, tipus)}
    </span>
  )
}

export default AccreditacioEntitySelector
